<script>
    import { apiMethods } from "../../Services/index.svelte";

    import {
        DirectInsuranceType,
        DirectPaymentStatus,
    } from "../../Utils/utilEnums.svelte";

    import {
        getDaysDifferFrom,
        getEmailValidation,
        getMobileValidation,
        isObjectEmpty,
        showError,
    } from "../../Utils/utilFunctions.svelte";

    import { DateInput, NotificationModal } from "./index.svelte";

    import LoadingModal from "./LoadingModal.svelte";

    export let data = {};
    export let paymentsData = [];
    export let isModalOpen = false;
    export let afterSendFunc = () => {};

    $: if (isModalOpen) initData();
    else {
        sendToPatientConfirmAction = null;
        sendingPrompt = "";
        finishPrompt = "";
        currentId = null;
    }

    $: if (sendToPatientInPastTime) {
        const sendToPatientTimeInput = document.getElementById(
            "sendToPatientTimeInput",
        );
        if (sendToPatientTimeInput)
            sendToPatientTimeInput.style.outline = "red solid 2px";
    } else {
        const sendToPatientTimeInput = document.getElementById(
            "sendToPatientTimeInput",
        );
        if (sendToPatientTimeInput) sendToPatientTimeInput.style.outline = "";
    }

    let isSending = false;

    let sendToPatientConfirmAction,
        collectSendToPatientModalOpen = false,
        sendToPatientInPastTime = false;

    let sendingPrompt = "",
        finishPrompt = "",
        showFinishModal = false;

    let confirmSendToSupportModalOpen = false,
        currentId;

    let confirmSendLatestPaymentToPatientModalOpen = false;

    let scheduledSendOption;

    let sendingType = "";

    let hasInvoiceFullyPaid = false;

    const __SEND_DELAY_MINUTES__ = 5;

    const showClaimViaSendingInvoiceInsuranceTypes = [16, 17, 18, 25, 26, 30];

    const initData = async () => {
        const { type } = data;

        sendingType = type;

        switch (type) {
            case "quote":
                await _initQuote(data);
                break;
            case "invoice":
                await _initInvoice(data);
                break;
            case "payment":
            default:
                break;
        }
    };

    const sendQuoteToPatient = async (data) => {
        const isFeeEstimate = data.isFeeEstimate;

        sendingPrompt = `Sending ${isFeeEstimate ? "Fee Estimate" : "Quote"}...`;
        _closeCollectSendToPatientModal();

        isSending = true;

        const { sendDelayMinutes } = data;

        if (sendDelayMinutes) data.sendDelayMinutes = sendDelayMinutes;

        await apiMethods.postMethod
            .sendEstimateToPatient(data)
            .then(async (res) => {
                if (!res || res.error)
                    showError(
                        res.error ??
                            `Failed to send ${isFeeEstimate ? "fee estimate" : "quote"} to patient, please refresh the page and try again and contact support if the issue persists.`,
                    );
                else {
                    if (
                        showClaimViaSendingInvoiceInsuranceTypes.includes(
                            data.insuranceType,
                        )
                    )
                        finishPrompt = `${isFeeEstimate ? "Fee Estimate" : "Quote"} sent.`;
                    else if (sendDelayMinutes)
                        finishPrompt = `${isFeeEstimate ? "Fee Estimate" : "Quote"} will be sent to the patient in ${_getDelayMinsText(sendDelayMinutes)}`;
                    else
                        finishPrompt = `${isFeeEstimate ? "Fee Estimate" : "Quote"} sent.`;

                    await afterSendFunc();
                    _showFinishModal();
                }
            });

        isSending = false;
    };

    const sendInvoiceToPatient = async (data) => {
        _closeCollectSendToPatientModal();

        sendingPrompt = "Sending Invoice...";

        isSending = true;

        const { sendDelayMinutes } = data;

        if (sendDelayMinutes) data.sendDelayMinutes = sendDelayMinutes;

        await apiMethods.postMethod
            .sendInvoiceToPatient(data)
            .then(async (res) => {
                if (!res || res.error)
                    showError(
                        res.error ??
                            "Failed to send invoice, please refresh the page and try again and contact support if the issue persists.",
                    );
                else {
                    if (
                        showClaimViaSendingInvoiceInsuranceTypes.includes(
                            data.insuranceType,
                        )
                    )
                        finishPrompt = "Invoice sent.";
                    else if (sendDelayMinutes)
                        finishPrompt = `Invoice will be sent to the patient in ${_getDelayMinsText(sendDelayMinutes)}`;
                    else finishPrompt = "Invoice sent.";

                    await afterSendFunc();
                    _showFinishModal();
                }
            });

        isSending = false;
    };

    const sendReceiptToPatient = async (receiptId) => {
        sendingPrompt = "Sending Receipt...";
        _closeConfirmSendLatestPaymentToPatientModal();

        isSending = true;

        await apiMethods.getMethod
            .getReceiptById(receiptId)
            .then(async (res) => {
                if (!res || res.error)
                    showError(
                        res.error ??
                            "Failed to load receipt details, please refresh the page and try again and contact support if the issue persists.",
                    );
                else
                    await apiMethods.postMethod
                        .sendReceiptToPatient(res)
                        .then(async (res) => {
                            if (!res || res.error)
                                showError(
                                    res.error ??
                                        "Failed to send receipt to patient, please refresh the page and try again and contact support if the issue persists.",
                                );
                            else {
                                finishPrompt = "Receipt sent to patient.";
                                _showFinishModal();
                                await afterSendFunc();
                            }
                        });
            });

        isSending = false;
    };

    const proceedSendQuoteOrInvoiceToSupport = async () => {
        if (currentId) {
            data.type == "quote" && (await sendQuoteToPatient(data));
            data.type == "invoice" && (await sendInvoiceToPatient(data));
        } else
            showError(
                `${data.type == "quote" ? "Quote" : data.type == "invoice" ? "Invoice" : ""} ID is missing, please refresh the page and try again and contact support if the issue persists.`,
            );
        _closeConfirmSendToSupportModal();
    };

    const confirmSendLatestPaymentToPatient = async () => {
        if (!hasInvoiceFullyPaid && data.type == "invoice") {
            _closeConfirmSendLatestPaymentToPatientModal();
            await sendInvoiceToPatient(data);
            return;
        }

        const latestPaymentPaidByPatient = paymentsData
            .filter(
                (payment) =>
                    payment.paidBy == 0 && [1, 2].includes(payment.status),
            )
            .reduce((latest, curr) =>
                new Date(curr.receivedDate) > new Date(latest.receivedDate)
                    ? curr
                    : latest,
            );

        if (
            !latestPaymentPaidByPatient ||
            isObjectEmpty(latestPaymentPaidByPatient)
        )
            return;

        _closeConfirmSendLatestPaymentToPatientModal();

        await sendReceiptToPatient(latestPaymentPaidByPatient.receiptId);
    };

    // HELPER FUNCTIONS
    const _initQuote = async (data) => {
        const {
            id: quoteId,
            insuranceType,
            patientPhone,
            patientEmail,
            isFromTable,
            billingDetails,
            skipDelay,
            patientId,
            patientContacts,
        } = data;

        if (
            showClaimViaSendingInvoiceInsuranceTypes.includes(insuranceType) ||
            skipDelay
        ) {
            // such insurance types are NOT sending invoice to patient, NO NEED to check patient contact details
            // skipDelay defined in view encounter billing tab - create quote and confirm send quote to patient right after quote creation process, no need to specify delay
            sendQuoteToPatient(data);
            return;
        }

        const isPhoneValid = getMobileValidation(
            isFromTable ? (billingDetails?.phone ?? "") : patientPhone,
        );
        const isEmailValid = getEmailValidation(
            isFromTable ? (billingDetails?.email ?? "") : patientEmail,
        );

        if (!isPhoneValid && !isEmailValid) {
            let contacts = patientContacts;

            if (!contacts)
                await apiMethods.getMethod
                    .getPatientById(patientId)
                    .then((res) => {
                        if (!res || res.error)
                            showError(
                                res.error ??
                                    "Failed to get patient contact details, please refresh the page and try again and contact support if the issue persists.",
                            );
                        else contacts = res.patientContacts;
                    });

            if (
                !contacts ||
                !contacts.length ||
                !contacts.some((contact) => contact.useAsContact)
            ) {
                _showConfirmSendToSupportModal(quoteId);
                return;
            }

            const contactPerson = contacts.find(
                (contact) => contact.useAsContact,
            );

            const isContactPhoneValid = getMobileValidation(
                contactPerson?.phone ?? "",
            );
            const isContactEmailValid = getEmailValidation(
                contactPerson?.email ?? "",
            );

            if (!isContactPhoneValid && !isContactEmailValid) {
                _showConfirmSendToSupportModal(quoteId);
                return;
            }
        }

        _openCollectSendToPatientModal(() => sendQuoteToPatient(data));
       
    };

    const _initInvoice = async (data) => {
        const {
            id: invoiceId,
            insuranceType,
            patientPhone,
            patientEmail,
            isFromTable,
            billingDetails,
            skipDelay,
            patientId,
            patientContacts,
            sendLatestReceipt,
        } = data;

        if (
            showClaimViaSendingInvoiceInsuranceTypes.includes(insuranceType) ||
            skipDelay
        ) {
            // such insurance types are NOT sending invoice to patient, NO NEED to check patient contact details
            // skipDelay defined in view encounter billing tab - create quote and confirm send quote to patient right after quote creation process, no need to specify delay
            sendInvoiceToPatient(data);
            return;
        }

        const isPhoneValid = getMobileValidation(
            isFromTable ? (billingDetails?.phone ?? "") : patientPhone,
        );
        const isEmailValid = getEmailValidation(
            isFromTable ? (billingDetails?.email ?? "") : patientEmail,
        );
        if (!isPhoneValid && !isEmailValid) {
            let contacts = patientContacts;

            if (!contacts)
                await apiMethods.getMethod
                    .getPatientById(patientId)
                    .then((res) => {
                        if (!res || res.error)
                            showError(
                                res.error ??
                                    "Failed to get patient contact details, please refresh the page and try again and contact support if the issue persists.",
                            );
                        else contacts = res.patientContacts;
                    });

            if (
                !contacts ||
                !contacts.length ||
                !contacts.some((contact) => contact.useAsContact)
            ) {
                _showConfirmSendToSupportModal(invoiceId);
                return;
            }

            const contactPerson = contacts.find(
                (contact) => contact.useAsContact,
            );

            const isContactPhoneValid = getMobileValidation(
                contactPerson?.phone ?? "",
            );
            const isContactEmailValid = getEmailValidation(
                contactPerson?.email ?? "",
            );

            if (!isContactPhoneValid && !isContactEmailValid) {
                _showConfirmSendToSupportModal(invoiceId);
                return;
            }
        }

        const sendLatestPaymentToPatient = _determineSendLatestPaymentToPatient(
            data,
            paymentsData,
        );
        if (sendLatestPaymentToPatient) {
            _openConfirmSendLatestPaymentToPatientModal();
            return;
        }

        if (!sendLatestReceipt) {
            //open collet to patient only if invoice oopType = postpay and patientPaysTotalFee = true, or invoice oopType = postpay and patientPaysTotalFee = false and gap > 0
            if(data.oopType == 1) {
                if(data.patientPaysTotalFee || (!data.patientPaysTotalFee && data.actualGap > 0)) {
                    _openCollectSendToPatientModal(() => sendInvoiceToPatient(data));
                }
            } else {
                //send invoice
                sendInvoiceToPatient(data);
            }
        }
           
        else {
            const latestReceipt = paymentsData?.find(
                (payment) =>
                    payment.paidBy == 0 && [1, 2].includes(payment.status),
            );

            if (latestReceipt?.receiptId)
                await sendReceiptToPatient(latestReceipt.receiptId);
        }
    };

    const _handleSendOptionChange = () => {
        sendToPatientInPastTime = false;

        if (scheduledSendOption == 1 || scheduledSendOption == 2 || scheduledSendOption == 4)
            data.sendDate = getDaysDifferFrom(scheduledSendOption == 1 ? 1 : (scheduledSendOption == 2 ? 3 : 7));
        else if (scheduledSendOption == 3) data.sendDate = new Date();

        _handleSendTimeChange();
    };

    const _handleSendTimeChange = () => {
        sendToPatientInPastTime = false;

        const { sendTime, sendDate } = data;

        let diffDays = 0;

        if (scheduledSendOption && sendDate)
            diffDays = _getDaysFromTomorrow(sendDate);

        const now = new Date();
        const [sendHour, sendMinute] = sendTime.split(":").map(Number);

        const currentHour = now.getHours();
        const currentMinute = now.getMinutes();

        const currentTime = new Date();
        currentTime.setHours(currentHour, currentMinute, 0, 0);

        const futureTime = new Date();
        if (diffDays > 0) {
            const sendDateParts = new Date(sendDate);
            futureTime.setFullYear(
                sendDateParts.getFullYear(),
                sendDateParts.getMonth(),
                sendDateParts.getDate(),
            );
        }
        futureTime.setHours(sendHour, sendMinute, 0, 0);

        if (
            diffDays === 0 &&
            futureTime < currentTime &&
            now.toDateString() === new Date().toDateString()
        ) {
            sendToPatientInPastTime = true;
            return;
        }

        if (sendHour === currentHour && sendMinute === currentMinute) {
            data.sendDelayMinutes = 0;
            return;
        }

        const timeDiffMins = Math.floor((futureTime - currentTime) / 1000 / 60);

        data.sendDelayMinutes =
            scheduledSendOption == 1 || scheduledSendOption == 2 || scheduledSendOption == 4
                ? timeDiffMins - __SEND_DELAY_MINUTES__
                : timeDiffMins;
    };

    const _getDaysFromTomorrow = (date) => {
        const inputDate = new Date(date);
        const inputYear = inputDate.getFullYear();
        const inputMonth = inputDate.getMonth();
        const inputDay = inputDate.getDate();

        const inputDateNormalized = new Date(inputYear, inputMonth, inputDay);

        const now = new Date();
        const today = new Date(
            now.getFullYear(),
            now.getMonth(),
            now.getDate(),
        );

        const tomorrow = new Date(today);
        tomorrow.setDate(today.getDate() + 1);

        if (inputDateNormalized < tomorrow) return 0;

        const diffInMs = inputDateNormalized.getTime() - tomorrow.getTime();
        const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24)) + 1;

        return diffInDays;
    };

    const _determineSendLatestPaymentToPatient = (
        invoiceData = {},
        payments = [],
    ) => {
        const {
            oopType,
            patientInsuranceType,
            patientPaysTotalFee,
            actualGap,
        } = invoiceData;

        // hide send invoice icon if all cases fulfilled
        // 1. oopType is PrePay (oopType == 0)
        // 2. patient paid > 0 (payments paidBy == 0 && status of payment is Received or Received Externally)
        // 3. insurance type is Cosmetic or (is Private with !patientPaysTotalFee and gapFee > 0)
        const isOOPType = oopType == 0;
        const patientPaysOverZero =
            payments.length &&
            payments.reduce(
                (sum, { amount, paidBy, status }) =>
                    sum + (paidBy == 0 && [1, 2].includes(status) ? amount : 0),
                0,
            ) > 0;
        const isConditionalInsuranceTypes =
            patientInsuranceType == DirectInsuranceType.Cosmetic ||
            (patientInsuranceType == DirectInsuranceType.Private &&
                !patientPaysTotalFee &&
                actualGap > 0);

        return isOOPType && patientPaysOverZero && isConditionalInsuranceTypes;
    };

    const _openCollectSendToPatientModal = (action = () => {}) => {
        sendToPatientConfirmAction = () => {
            _handleSendTimeChange();
            action();
        };
        collectSendToPatientModalOpen = true;

        const now = new Date();
        now.setMinutes(now.getMinutes() + __SEND_DELAY_MINUTES__);

        let hours = now.getHours();
        let mins = now.getMinutes();

        hours = hours < 10 ? `0${hours}` : hours;
        mins = mins < 10 ? `0${mins}` : mins;

        data.sendTime = `${hours}:${mins}`;
    };

    const _closeCollectSendToPatientModal = () => {
        sendToPatientConfirmAction = null;
        collectSendToPatientModalOpen = false;
        isModalOpen = false;
        sendToPatientInPastTime = false;
        scheduledSendOption = "0";
    };

    const _showFinishModal = () => (showFinishModal = true);

    const _closeFinishModal = () => {
        showFinishModal = false;
        isModalOpen = false;
    };

    const _showConfirmSendToSupportModal = (id) => {
        confirmSendToSupportModalOpen = true;
        currentId = id;
    };

    const _closeConfirmSendToSupportModal = () => {
        confirmSendToSupportModalOpen = false;
        currentId = null;
        isModalOpen = false;
    };

    const _openConfirmSendLatestPaymentToPatientModal = () => {
        if (data.type == "invoice") {
            const { totalFee, payments } = data;

            const totalInvoicePaid = payments.reduce((sum, payment) => {
                const { amount, status } = payment;

                if (
                    status == DirectPaymentStatus.Received ||
                    status == DirectPaymentStatus.ReceivedExternally
                )
                    sum += amount;

                return sum;
            }, 0);

            const epsilon = 0.01;

            hasInvoiceFullyPaid =
                Math.abs(totalFee - totalInvoicePaid) < epsilon;
        }

        confirmSendLatestPaymentToPatientModalOpen = true;
    };

    const _closeConfirmSendLatestPaymentToPatientModal = () => {
        confirmSendLatestPaymentToPatientModalOpen = false;
        isModalOpen = false;
    };

    const _getDelayMinsText = (delayMins = 0) => {
        if (!delayMins) return `0 min`;

        let remain = delayMins;

        const days = Math.floor(remain / (24 * 60));
        remain = remain - days * 24 * 60;

        const hours = Math.floor(remain / 60);
        const mins = remain - hours * 60;

        return `${days ? `${days > 1 ? `${days} days` : "1 day"} ` : ""}${hours ? `${hours > 1 ? `${hours} hours` : "1 hour"} ` : ""}${mins ? `${mins > 1 ? `${mins} mins` : "1 min"} ` : ""}`;
    };

    
</script>

{#if isSending}
    <LoadingModal titleText={sendingPrompt} />
{/if}

<!-- Finish Modal -->
<NotificationModal
    isModalOpen={showFinishModal}
    title={`Send ${sendingType} to Patient`}
    submitText="Done"
    showCancelBtn={false}
    confirmAction={_closeFinishModal}
    closeModalFunction={_closeFinishModal}
>
    <p class="text-secondary whitespace-pre-line">{finishPrompt}</p>
</NotificationModal>

<!-- Send Quote or Invoice to Support Confirmation Modal -->
<NotificationModal
    isModalOpen={confirmSendToSupportModalOpen}
    cancelText="Cancel"
    submitText="Confirm"
    confirmAction={proceedSendQuoteOrInvoiceToSupport}
    closeModal={_closeConfirmSendToSupportModal}
    closeModalFunction={_closeConfirmSendToSupportModal}
>
    <p class="text-error">
        The email and phone details are missing, this message will not be sent
        to the patient. You can choose to send this to the support team for
        follow up.
    </p>
</NotificationModal>

<!-- Send Latest Receipt to Patient Confirmation Modal -->
<NotificationModal
    isModalOpen={confirmSendLatestPaymentToPatientModalOpen}
    title="Send the final invoice and receipt to patient?"
    cancelText="Cancel"
    submitText="Send"
    confirmAction={confirmSendLatestPaymentToPatient}
    closeModal={_closeConfirmSendLatestPaymentToPatientModal}
    closeModalFunction={_closeConfirmSendLatestPaymentToPatientModal}
>
    <p>The patient already paid for the preliminary invoice.</p>
    <p>Do you want to send the final invoice and receipt?</p>
</NotificationModal>

<!-- Collect Send to Patient Delay Minutes Modal -->
<NotificationModal
    isModalOpen={collectSendToPatientModalOpen}
    title={`Send ${data?.isFeeEstimate ? "Fee Estimate" : (data?.type ?? "")} to patient`}
    cancelText="cancel"
    closeModal={_closeCollectSendToPatientModal}
    closeModalFunction={_closeCollectSendToPatientModal}
    contentClass={"p-6 flex flex-col"}
    isConfirming={isSending}
    showConfirmBtn={false}
    btnOptions={[
        {
            text: isSending ? "Sending" : "Send",
            loading: isSending || sendToPatientInPastTime,
            function: () => sendToPatientConfirmAction(),
        },
    ]}
>
    <div class="flex flex-col gap-y-2">
        <div class="flex gap-x-2 w-full items-center">
            <p class="scheduledTitle">Schedule send at:</p>

            <select
                class="select select-sm select-bordered w-40"
                bind:value={scheduledSendOption}
                on:change={_handleSendOptionChange}
            >
                <option value="0" selected>Send Today</option>
                <option value="1">Send Tomorrow</option>
                <option value="2">Send in 3 Days</option>
                <option value="4">Send in 7 Days</option>
                <option value="3">Custom</option>
            </select>
        </div>

        <div class="flex gap-x-2 w-full items-center">
            {#if !scheduledSendOption || scheduledSendOption == 0}
                <p class="scheduledTitle">Send at:</p>

                <input
                    type="time"
                    class="input input-sm input-bordered w-40"
                    id="sendToPatientTimeInput"
                    bind:value={data.sendTime}
                    on:change={_handleSendTimeChange}
                    on:focus={({ target }) => (target.style.outline = "")}
                    disabled={isSending}
                />
            {:else if scheduledSendOption == 3}
                <p class="scheduledTitle">Send at:</p>

                <DateInput
                    bind:value={data.sendDate}
                    on:change={_handleSendTimeChange}
                    id="sendToPatientDateInput"
                    isDisable={isSending}
                    minDate={new Date()}
                    wrapperClass="input input-sm px-0 input-bordered flex-1"
                />

                <input
                    type="time"
                    class="input input-sm input-bordered w-40"
                    id="sendToPatientTimeInput"
                    bind:value={data.sendTime}
                    on:change={_handleSendTimeChange}
                    on:focus={({ target }) => (target.style.outline = "")}
                    disabled={isSending}
                />
            {/if}
        </div>

        {#if sendToPatientInPastTime}
            <div class="flex gap-x-2 w-full items-cente">
                <p class="scheduledTitle" />
                <p class="text-error">Please set a future time.</p>
            </div>
        {/if}
    </div>
</NotificationModal>

<style>
    .scheduledTitle {
        width: 150px;
    }
</style>
